import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faCheck,
  faFileExcel,
  faPen,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { ExcelExportComponent } from '@progress/kendo-angular-excel-export';
import {
  AddEvent,
  CancelEvent,
  DataBindingDirective,
  EditEvent,
  GridComponent,
  PageChangeEvent,
  RemoveEvent,
  SaveEvent,
} from '@progress/kendo-angular-grid';
import { SelectEvent } from '@progress/kendo-angular-upload';
import { process } from '@progress/kendo-data-query';
import { plusIcon, searchIcon, SVGIcon } from '@progress/kendo-svg-icons';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { LoadingService } from 'src/app/services/loading.service';
import { ReferenceService } from 'src/app/services/reference.service';
import { IReferenceData } from 'src/app/shared/interfaces/form/reference-data.model';
import {
  validateEmail,
  validateName,
} from 'src/app/shared/validators/validator';
import { categories, Category } from '../../../../modules/administration/constants/categories';
import { Product } from '../../../../modules/administration/constants/products';
import { AssessmentContractResponse } from '../../../../modules/administration/interfaces/admin-service-types';
import { AssessmentContractData, ExportAssessmentContract } from '../../../../modules/administration/models/export-assessment-contract-model';
import { SaveAssessmentContractModel } from '../../../../modules/administration/models/save-assesment-contract-model';
import { AdminService } from '../../../../modules/administration/services/admin.service';

const DEFAULT_PAGE_SIZE: number = 5;
const FA_ICON_PATH_INDEX: number = 4;
const FA_ICON_WIDTH_INDEX: number = 0;
const FA_ICON_HEIGHT_INDEX: number = 1;

@Component({
  selector: 'app-assessments-contract',
  templateUrl: './assessments-contract.component.html',
  styleUrls: ['./assessments-contract.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AssessmentsContractComponent {
  @ViewChild(DataBindingDirective) dataBinding: DataBindingDirective;
  @Input() locationDetails: Product;
  inactiveAssessmentContract$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public gridView: Product[];
  loadSpinner: boolean;
  agencyId: string;
  type: string;
  public plusIcon: SVGIcon = plusIcon;
  public checkIcon: SVGIcon = this.customFaIcon(faCheck);
  public xMarkIcons: SVGIcon = this.customFaIcon(faXmark);
  public penIcons: SVGIcon = this.customFaIcon(faPen);
  public fileIcons: SVGIcon = this.customFaIcon(faFileExcel);
  public searchIcon: SVGIcon = searchIcon;
  public gridData: Product[];
  public categories: Category[] = categories;
  public mySelection: string[] = [];
  public pageSize: number = DEFAULT_PAGE_SIZE;
  public skip: number = 0;
  public formGroup: FormGroup;
  private editedRowIndex: number;
  showRemoveandEditIcon: boolean;
  showRemoveAndEditIcon: boolean;
  public assessmentListSelection: string[] = [];
  assessmentContractExportData: ExportAssessmentContract[] = [];
  @ViewChild('excelexport') excelexport: ExcelExportComponent;
  createFormGroup: (dataItem: Product) => FormGroup = (dataItem: Product): FormGroup =>
    new FormGroup({
      agencyAssessmentContractSk: new FormControl(
        dataItem?.agencyAssessmentContractSk
      ),
      agencySk: new FormControl(dataItem?.agencySk),
      contractSk: new FormControl(dataItem?.contractSk),
      evaluationType: new FormControl(dataItem.evaluationType),
      languageId: new FormControl(dataItem.languageId),
      settingId: new FormControl(dataItem.settingId),
      serviceArea: new FormControl(dataItem.serviceArea),
      interpreterType: new FormControl(dataItem.interpreterType),
      awardedRate: new FormControl(dataItem.awardedRate),
      ownercontact: new FormControl(
        dataItem?.ownercontact,
        validateName(false)
      ),
      owneremail: new FormControl(dataItem?.owneremail, validateEmail(false)),
      ownerSe: new FormControl(dataItem?.ownerSe),
      tempContractNumber: new FormControl(dataItem?.tempContractNumber),
      agencyLocationSk: new FormControl(this.locationDetails.agencyLocationSk),

      // UnitsInStock: new FormContrrol(
      //   dataItem.UnitsInStock,
      //   Validators.compose([
      //     Validators.required,
      //     Validators.pattern("^[0-9]{1,3}"),
      //   ])
      // ),
      // CategoryID: new FormControl(dataItem.CategoryID),
    });
  settingsDropdown: IReferenceData[];
  serviceAreaDropdown: IReferenceData[];
  languageDropdown: IReferenceData[];
  interpreterTypeDropdown: IReferenceData[];
  assessUsedDropdown: IReferenceData[];
  assessmentDetails: AssessmentContractResponse;
  constructor(
    private adminService: AdminService,
    private route: ActivatedRoute,
    private loadingService: LoadingService,
    private referenceService: ReferenceService,
    private cdRef: ChangeDetectorRef
  ) {
    this.route.params.subscribe((params) => {
      this.agencyId = params.id;
    });
    this.getAssessmentList();
    this.getDropDownList();
  }
  getDropDownList(): void {
    this.loadingService.show();
    combineLatest([
      this.referenceService.getDropDownValues('Setting'),
      this.referenceService.getDropDownValues('ServiceArea'),
      this.referenceService.getDropDownValues('Language'),
      this.referenceService.getDropDownValues('InterpreterType'),
      this.referenceService.getDropDownValues('AssessUsed'),
    ]).subscribe(
      ([setting, serviceArea, language, interpreterType, assessUsed]) => {
        this.loadingService.hide();
        this.settingsDropdown = setting;
        this.serviceAreaDropdown = serviceArea;
        this.languageDropdown = language;
        this.interpreterTypeDropdown = interpreterType;
        this.assessUsedDropdown = assessUsed;
      }
    );
  }

  customFaIcon(icon: IconDefinition): SVGIcon {
    return {
      name: icon.iconName,
      content: `<path d="${icon.icon[FA_ICON_PATH_INDEX]}" />`,
      viewBox: `0 0 ${icon.icon[FA_ICON_WIDTH_INDEX]} ${icon.icon[FA_ICON_HEIGHT_INDEX]}`,
    };
  }

  public onAgencyGridFilter(value: Event): void {
    const inputValue: Event = value;
    if (inputValue) {
      this.gridView = process(this.gridView, {
        filter: {
          logic: 'or',
          filters: [
            {
              field: 'evaluationType',
              operator: 'contains',
              value: inputValue,
            },
            {
              field: 'languageId',
              operator: 'contains',
              value: inputValue,
            },
            {
              field: 'settingId',
              operator: 'contains',
              value: inputValue,
            },
            {
              field: 'serviceArea',
              operator: 'contains',
              value: inputValue,
            },
            {
              field: 'interpreterType',
              operator: 'contains',
              value: inputValue,
            },
            {
              field: 'awardedRate',
              operator: 'contains',
              value: inputValue,
            },
            {
              field: 'ownercontact',
              operator: 'contains',
              value: inputValue,
            },
            {
              field: 'owneremail',
              operator: 'contains',
              value: inputValue,
            },
            {
              field: 'ownerSe',
              operator: 'contains',
              value: inputValue,
            },
            {
              field: 'tempContractNumber',
              operator: 'contains',
              value: inputValue,
            },
          ],
        },
      }).data;

      this.dataBinding.skip = 0;
    } else {
      this.getAssessmentList();
    }
  }
  getAssessmentList(): void {
    this.loadingService.show();
    this.adminService
      .getAssessmentsContractList(this.agencyId)
      .subscribe((res: AssessmentContractResponse) => {
        this.assessmentDetails = res;
        this.gridData = this.assessmentDetails?.agencyAssesmentContracts;
        const assessMent: ExportAssessmentContract[] = this.gridData?.map(
          (data: Product) =>
            new ExportAssessmentContract(data as unknown as AssessmentContractData, {
              assessUsedDropdown: this.assessUsedDropdown,
              languageDropdown: this.languageDropdown,
              settingsDropdown: this.settingsDropdown,
              serviceAreaDropdown: this.serviceAreaDropdown,
              interpreterTypeDropdown: this.interpreterTypeDropdown,
            })
        );
        this.assessmentContractExportData = assessMent;
        this.cdRef.markForCheck();
        this.loadingService.hide();
      });
  }

  onAssessmentListSelectionChange(event: any): void {
    void event;
  }

  public pageChange(event: PageChangeEvent): void {
    this.skip = event.skip;
  }
  public dropdownData(
    id: number | string,
    dropDownName: IReferenceData[]
  ): IReferenceData | undefined {
    return dropDownName?.find((x: IReferenceData) => x.id === id);
  }

  public addHandler({ sender }: AddEvent): void {
    this.closeEditor(sender);

    this.formGroup = this.createFormGroup({
      agencySk: this.route.snapshot.paramMap.get('id'),
      evaluationType: null,
      languageId: null,
      settingId: null,
      serviceArea: null,
      interpreterType: null,
      awardedRate: null,
      owneremail: null,
      ownerSe: null,
      tempContractNumber: null,
      agencyLocationSk: this.locationDetails.agencyLocationSk,
    });

    sender.addRow(this.formGroup);
  }

  public editHandler({ sender, rowIndex, dataItem }: EditEvent): void {
    this.closeEditor(sender);

    this.formGroup = this.createFormGroup(dataItem);

    this.editedRowIndex = rowIndex;

    sender.editRow(rowIndex, this.formGroup);
  }

  public cancelHandler({ sender, rowIndex }: CancelEvent): void {
    this.closeEditor(sender, rowIndex);
  }

  public saveHandler({ sender, rowIndex, formGroup, isNew }: SaveEvent): void {
    void isNew;
    this.loadingService.show();
    const product: SaveAssessmentContractModel = new SaveAssessmentContractModel(formGroup.value);

    this.adminService.addAgency(product).subscribe((data: boolean) => {
      if (data) {
        this.loadingService.hide();
        this.getAssessmentList();
        this.assessmentListSelection = [];
      }
    });

    sender.closeRow(rowIndex);
  }
  inactiveAssessmentsContract(): void {
    this.inactiveAssessmentContract$.next(true);
  }
  closeInactiveAssessmentContractDialog(status: boolean): void {
    this.inactiveAssessmentContract$.next(status);
    this.getAssessmentList();
    this.assessmentListSelection = [];
  }
  showEditIcon(): void {
    this.showRemoveAndEditIcon = true;
  }
  public removeHandler({ dataItem }: RemoveEvent): void {
    void dataItem;
  }
  uploadFile(ev: SelectEvent): void {
    this.adminService.validateAndUploadFile(ev);
  }

  private closeEditor(
    grid: GridComponent,
    rowIndex: number = this.editedRowIndex
  ): void {
    grid.closeRow(rowIndex);
    this.editedRowIndex = undefined;
    this.formGroup = undefined;
  }
}
